import React from "react";
import {
  ShoppingCart,
  Phone,
  Package,
  X,
  CheckCircle,
  Clock,
  TrendingUp,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { OrderStats, User, StatusInfo } from "./types";

interface StatusBreakdownProps {
  stats: OrderStats | null;
  currentUser?: User;
}

const StatusBreakdown: React.FC<StatusBreakdownProps> = ({
  stats,
  currentUser,
}) => {
  const getStatusInfo = (status: number): StatusInfo => {
    const statusMap = {
      1: {
        name: "New Orders",
        color: "bg-blue-100 text-blue-800",
        icon: ShoppingCart,
      },
      2: {
        name: "Processing",
        color: "bg-yellow-100 text-yellow-800",
        icon: Clock,
      },
      3: {
        name: "Callback",
        color: "bg-orange-100 text-orange-800",
        icon: Phone,
      },
      4: {
        name: "Hold",
        color: "bg-gray-100 text-gray-800",
        icon: AlertCircle,
      },
      5: { name: "Cancelled", color: "bg-red-100 text-red-800", icon: X },
      6: {
        name: "Packaging",
        color: "bg-purple-100 text-purple-800",
        icon: Package,
      },
      7: {
        name: "Sending",
        color: "bg-indigo-100 text-indigo-800",
        icon: TrendingUp,
      },
      8: {
        name: "Transfer",
        color: "bg-cyan-100 text-cyan-800",
        icon: RefreshCw,
      },
      9: {
        name: "Arrived",
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
      },
      10: {
        name: "Completed",
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
      },
    };
    return (
      statusMap[status as keyof typeof statusMap] || {
        name: "Unknown",
        color: "bg-gray-100 text-gray-800",
        icon: AlertCircle,
      }
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
        <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
        {currentUser?.role === "country-post-manager"
          ? "Shipping Status Breakdown"
          : "Order Status Breakdown"}
      </h3>
      <div className="space-y-3">
        {stats?.byStatus &&
          Object.entries(stats.byStatus)
            .filter(([status]) => {
              // For country-post-manager, only show shipping-related statuses
              if (currentUser?.role === "country-post-manager") {
                const shippingStatuses = [6, 7, 8, 9, 10, 25, 30]; // Packaging, Sending, Transfer, Arrived, Completed, Returned, Failed
                return shippingStatuses.includes(parseInt(status));
              }
              return true; // Show all statuses for other roles
            })
            .map(([status, count]) => {
              const statusInfo = getStatusInfo(parseInt(status));
              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <statusInfo.icon className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">
                      {statusInfo.name}
                    </span>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}
                  >
                    {count}
                  </span>
                </div>
              );
            })}
      </div>
    </div>
  );
};

export default StatusBreakdown;
